import { useEffect, useMemo, useState } from "react";

import { addOpacity } from "Utilities/Colors/colorUtils";

const randomInRange = (min, max) => Math.random() * (max - min) + min;

/**
 * Returns an inline style object with a box-shadow that slowly wanders
 * between opacity values at randomized intervals.
 *
 * @param {string} color - Base glow color (hex or rgb, no alpha)
 * @param {Object} [options]
 * @param {number} [options.blur=6] - Shadow blur radius in px
 * @param {number} [options.spread=2] - Shadow spread radius in px
 * @param {number} [options.minOpacity=0.08] - Lowest opacity the glow can reach
 * @param {number} [options.maxOpacity=0.4] - Highest opacity the glow can reach
 * @param {number} [options.minInterval=2000] - Shortest pause between changes (ms)
 * @param {number} [options.maxInterval=4000] - Longest pause between changes (ms)
 * @returns {{ boxShadow: string, transition: string }}
 */
const useWanderingGlow = (
  color,
  {
    blur = 6,
    spread = 2,
    minOpacity = 0.08,
    maxOpacity = 0.4,
    minInterval = 2000,
    maxInterval = 4000,
  } = {}
) => {
  const [opacity, setOpacity] = useState(() =>
    randomInRange(minOpacity, maxOpacity)
  );
  const [duration, setDuration] = useState(() =>
    randomInRange(minInterval, maxInterval)
  );

  useEffect(() => {
    const id = setTimeout(() => {
      setOpacity(randomInRange(minOpacity, maxOpacity));
      setDuration(randomInRange(minInterval, maxInterval));
    }, duration);
    return () => clearTimeout(id);
  }, [opacity, duration, minOpacity, maxOpacity, minInterval, maxInterval]);

  return useMemo(
    () => ({
      boxShadow: `0 0 ${blur}px ${spread}px ${addOpacity(color, opacity)}`,
      transition: `box-shadow ${duration / 1000}s ease-in-out`,
    }),
    [blur, spread, color, opacity, duration]
  );
};

export default useWanderingGlow;
